/**
 * /sites/:siteId/keywords — Danny's keyword queue (oneshot flow).
 *
 * Add keyword + optional angle note + optional reader-quotes paste.
 * Generate fires the cron step-machine (~7 min); status auto-refreshes.
 * On 'review' the row links to the draft in the editor. Failed rows
 * show the reason + Retry.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, ApiError } from '../lib/api.ts'

type ParsedRow = { keyword: string; h1?: string; meta?: string }

// Parse one pasted line. Accepts a bare keyword, keyword<tab>h1<tab>meta,
// or a full spreadsheet row tier<tab>bucket<tab>language<tab>keyword<tab>h1<tab>meta.
// Spreadsheet copy-paste delimits columns with TABs. Returns null for blank
// lines and the header row.
function parseRow(line: string): ParsedRow | null {
  const cols = line.split('\t').map((c) => c.trim())
  let row: ParsedRow
  if (cols.length >= 6 && /^\d+$/.test(cols[0])) {
    row = { keyword: cols[3], h1: cols[4] || undefined, meta: cols[5] || undefined }
  } else if (cols.length >= 3) {
    row = { keyword: cols[0], h1: cols[1] || undefined, meta: cols[2] || undefined }
  } else {
    row = { keyword: line }
  }
  const k = row.keyword.toLowerCase()
  if (!row.keyword || k === 'keyword' || k === 'tier' || k === 'h1_title') return null
  return row
}

interface KeywordRow {
  id: string
  keyword: string
  note: string | null
  status: 'queued' | 'generating' | 'review' | 'failed'
  draft_id: string | null
  error: string | null
  cluster: string | null
  is_pillar: number
  has_voc: number
  has_h1: number
  created_at: number
  updated_at: number
}

const STATUS_LABEL: Record<KeywordRow['status'], string> = {
  queued: 'Queued',
  generating: 'Writing…',
  review: 'Ready to review',
  failed: 'Failed',
}

export function KeywordsScreen() {
  const { siteId = 'll' } = useParams()
  const [rows, setRows] = useState<KeywordRow[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [keyword, setKeyword] = useState('')
  const [note, setNote] = useState('')
  const [voc, setVoc] = useState('')
  const [busy, setBusy] = useState(false)
  const [autoStart, setAutoStart] = useState(true)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [budget, setBudget] = useState<{ spentCents: number; capCents: number; paused: boolean } | null>(null)
  const [queuedCount, setQueuedCount] = useState(0)
  const [releasing, setReleasing] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    refresh()
    timer.current = window.setInterval(refresh, 20_000)
    return () => {
      if (timer.current) window.clearInterval(timer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId])

  async function refresh() {
    try {
      const res = await api<{
        keywords: KeywordRow[]
        budget?: { spentCents: number; capCents: number; paused: boolean }
        queuedCount?: number
      }>(`/api/sites/${siteId}/keywords`)
      setRows(res.keywords)
      if (res.budget) setBudget(res.budget)
      setQueuedCount(res.queuedCount ?? 0)
      // Map draft ids to slugs so review rows can deep-link to the editor.
      const need = res.keywords.filter((k) => k.draft_id)
      if (need.length > 0) {
        const grid = await api<{ cards: { id?: string | null; slug: string }[] }>(
          `/api/sites/${siteId}/drafts`,
        ).catch(() => null)
        if (grid) {
          const map: Record<string, string> = {}
          for (const a of grid.cards) if (a.id) map[a.id] = a.slug
          setDrafts(map)
        }
      }
    } catch {
      /* keep last view */
    }
  }

  // Live preview of what the paste will become, so the column mapping is
  // visible before anything is created (operator trust gap, 2026-06-13).
  const preview = useMemo(() => {
    const lines = keyword.split('\n').map((l) => l.trim()).filter(Boolean)
    const parsed = lines.map(parseRow).filter((r): r is ParsedRow => r !== null)
    return {
      lineCount: lines.length,
      rows: parsed,
      withH1: parsed.filter((r) => r.h1).length,
      withMeta: parsed.filter((r) => r.meta).length,
      skipped: lines.length - parsed.length,
    }
  }, [keyword])

  async function add() {
    const lines = keyword
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
    if (lines.length === 0) return
    setBusy(true)
    setError('')
    setInfo('')

    // Single line: keep the rich path (angle note + reader quotes apply).
    if (lines.length === 1) {
      const row = parseRow(lines[0])
      if (!row) {
        setBusy(false)
        return
      }
      try {
        const created = await api<{ id: string }>(`/api/sites/${siteId}/keywords`, {
          method: 'POST',
          body: JSON.stringify({
            keyword: row.keyword,
            h1: row.h1,
            meta: row.meta,
            note: note.trim() || undefined,
            voc: voc.trim() || undefined,
          }),
        })
        if (autoStart) {
          await api(`/api/sites/${siteId}/keywords/${created.id}/generate`, { method: 'POST' })
        }
        setKeyword('')
        setNote('')
        setVoc('')
        await refresh()
      } catch (e) {
        const detail =
          e instanceof ApiError && typeof (e.body as { detail?: string })?.detail === 'string'
            ? (e.body as { detail: string }).detail
            : (e as Error).message
        setError(detail)
      } finally {
        setBusy(false)
      }
      return
    }

    // Many lines (a spreadsheet paste): one bulk request, imported as
    // queued. Auto-start is intentionally ignored here — review a sample,
    // then release the batch with "Generate all queued".
    try {
      const parsed = lines.map(parseRow).filter((r): r is { keyword: string; h1?: string; meta?: string } => r !== null)
      const res = await api<{ added: number; duplicates: number; skipped: number }>(
        `/api/sites/${siteId}/keywords/bulk`,
        { method: 'POST', body: JSON.stringify({ rows: parsed }) },
      )
      setKeyword('')
      setInfo(
        `Imported ${res.added} keyword(s) as queued${res.duplicates ? `, skipped ${res.duplicates} duplicate(s)` : ''}. Generate a couple to check quality, then "Generate all queued" to release the rest.`,
      )
      await refresh()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function generateAllQueued() {
    if (!confirm(`Start writing all ${queuedCount} queued keyword(s)? They drip out within your daily budget.`)) return
    setReleasing(true)
    setError('')
    setInfo('')
    try {
      const res = await api<{ released: number }>(`/api/sites/${siteId}/keywords/generate-all-queued`, {
        method: 'POST',
      })
      setInfo(`Released ${res.released} keyword(s). Writing now, paced by the daily budget.`)
      await refresh()
    } catch (e) {
      const detail =
        e instanceof ApiError && typeof (e.body as { error?: string })?.error === 'string'
          ? (e.body as { error: string }).error
          : (e as Error).message
      setError(detail)
    } finally {
      setReleasing(false)
    }
  }

  async function action(id: string, verb: 'generate' | 'retry') {
    setError('')
    try {
      await api(`/api/sites/${siteId}/keywords/${id}/${verb}`, { method: 'POST' })
      await refresh()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${verb} failed`)
    }
  }

  return (
    <div className="screen">
      <h1>Keywords</h1>
      <p className="muted">
        Paste a keyword, optionally an angle and real reader quotes. Generate
        writes 3 variants, judges them against the gold standard, illustrates
        the winner, and drops it in Articles for your review (~7 min, ~RM 0.15).
      </p>

      <div className="card" style={{ display: 'grid', gap: 8 }}>
        <textarea
          placeholder={'Paste here. One keyword per line — OR paste rows straight from your sheet (keyword, headline, meta description). The box reads the columns automatically; the header row is ignored.'}
          rows={preview.lineCount > 1 ? 6 : 2}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        {preview.rows.length > 0 && (
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 10,
              background: 'var(--bg-muted)',
              fontSize: 13,
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <strong>{preview.rows.length}</strong> keyword{preview.rows.length === 1 ? '' : 's'} detected
              {preview.withH1 > 0 ? <> · <strong>{preview.withH1}</strong> with your headline</> : null}
              {preview.withMeta > 0 ? <> · <strong>{preview.withMeta}</strong> with meta</> : null}
              {preview.skipped > 0 ? <> · {preview.skipped} blank/header skipped</> : null}
            </div>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-faint)' }}>
                  <th style={{ padding: '2px 6px' }}>Keyword</th>
                  <th style={{ padding: '2px 6px' }}>Headline (H1)</th>
                  <th style={{ padding: '2px 6px' }}>Meta</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 5).map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '3px 6px' }}>{r.keyword}</td>
                    <td style={{ padding: '3px 6px', color: r.h1 ? 'var(--text)' : 'var(--text-faint)' }}>
                      {r.h1 ? r.h1.slice(0, 60) + (r.h1.length > 60 ? '…' : '') : 'writer chooses'}
                    </td>
                    <td style={{ padding: '3px 6px', color: r.meta ? 'var(--text)' : 'var(--text-faint)' }}>
                      {r.meta ? '✓ yours' : 'writer chooses'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.rows.length > 5 ? (
              <div style={{ color: 'var(--text-faint)', marginTop: 4 }}>
                + {preview.rows.length - 5} more…
              </div>
            ) : null}
          </div>
        )}
        <input
          placeholder="Angle note, optional (single keyword only)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <textarea
          placeholder="Reader quotes (VOC), optional — one per line. If empty, the Corpus store is searched."
          rows={5}
          value={voc}
          onChange={(e) => setVoc(e.target.value)}
        />
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={autoStart}
            onChange={(e) => setAutoStart(e.target.checked)}
          />
          Start writing immediately (no extra Generate click)
        </label>
        <button disabled={busy || !keyword.trim()} onClick={add}>
          {busy ? 'Adding…' : autoStart ? 'Add and start writing' : 'Add keyword(s)'}
        </button>
        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          Pasting many rows from a spreadsheet? They import as <strong>queued</strong> (not started) so you can
          check a sample first, then release them below.
        </p>
        {error ? <p style={{ color: 'crimson' }}>{error}</p> : null}
        {info ? <p style={{ color: 'var(--text-muted)' }}>{info}</p> : null}
      </div>

      {(queuedCount > 0 || budget) && (
        <div
          className="card"
          style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
        >
          <div style={{ fontSize: 14 }}>
            {queuedCount > 0 ? (
              <strong>{queuedCount} keyword(s) queued</strong>
            ) : (
              <span className="muted">No keywords queued</span>
            )}
            {budget ? (
              <span className="muted" style={{ marginLeft: 12 }}>
                Today's AI spend: ${(budget.spentCents / 100).toFixed(2)} / ${(budget.capCents / 100).toFixed(2)}
                {budget.paused ? ' — paused, resumes after midnight UTC' : ''}
              </span>
            ) : null}
          </div>
          {queuedCount > 0 ? (
            <button disabled={releasing} onClick={generateAllQueued}>
              {releasing ? 'Releasing…' : `Generate all ${queuedCount} queued`}
            </button>
          ) : null}
        </div>
      )}

      <table className="grid-table" style={{ marginTop: 16, width: '100%' }}>
        <thead>
          <tr>
            <th>Keyword</th>
            <th>Quotes</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((k) => (
            <tr key={k.id}>
              <td>
                <strong>{k.keyword}</strong>
                {k.cluster ? (
                  <div className="muted" style={{ fontSize: 12 }}>
                    {k.cluster}{k.is_pillar ? ' · pillar' : ''}
                  </div>
                ) : null}
                {k.note ? <div className="muted">{k.note}</div> : null}
                {k.status === 'failed' && k.error ? (
                  <div style={{ color: 'crimson', fontSize: 12 }}>{k.error}</div>
                ) : null}
              </td>
              <td>
                {k.has_voc ? 'pasted' : 'corpus'}
                {k.has_h1 ? <div className="muted">your title ✓</div> : null}
              </td>
              <td>{STATUS_LABEL[k.status] ?? k.status}</td>
              <td>
                {k.status === 'queued' ? (
                  <button onClick={() => action(k.id, 'generate')}>Generate</button>
                ) : k.status === 'failed' ? (
                  <button onClick={() => action(k.id, 'retry')}>Retry</button>
                ) : k.status === 'review' && k.draft_id && drafts[k.draft_id] ? (
                  <Link to={`/sites/${siteId}/articles/${drafts[k.draft_id]}`}>
                    Open draft
                  </Link>
                ) : k.status === 'review' ? (
                  <Link to={`/sites/${siteId}/articles`}>See Articles</Link>
                ) : (
                  <span className="muted">in progress…</span>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="muted">
                No keywords yet. Add your first above.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  )
}
